import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Edit2, Plus, Trash2 } from "lucide-react";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { Header } from "../components/layout/Header";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

const API = `${process.env.REACT_APP_BACKEND_URL || "http://localhost:8000"}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";

const initialForm = {
  name: "",
  address: "",
  phone: "",
  logo_path: "",
};

const resolveFileUrl = (path) => {
  const value = String(path || "").trim();
  if (!value) return "";
  if (value.startsWith("http://") || value.startsWith("https://")) return value;
  return `${BACKEND_URL}${value}`;
};

export default function Merchants() {
  const [merchants, setMerchants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingMerchant, setEditingMerchant] = useState(null);
  const [formData, setFormData] = useState(initialForm);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const sortedMerchants = useMemo(
    () => [...merchants].sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""))),
    [merchants]
  );

  const fetchMerchants = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/merchants`, { withCredentials: true });
      setMerchants(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to fetch merchants:", error);
      alert(error.response?.data?.detail || "Gagal mengambil data merchant.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMerchants();
  }, []);

  const openCreate = () => {
    setEditingMerchant(null);
    setFormData(initialForm);
    setIsModalOpen(true);
  };

  const openEdit = (merchant) => {
    setEditingMerchant(merchant);
    setFormData({
      name: String(merchant.name || ""),
      address: String(merchant.address || ""),
      phone: String(merchant.phone || ""),
      logo_path: String(merchant.logo_path || ""),
    });
    setIsModalOpen(true);
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const body = new FormData();
    body.append("file", file);
    try {
      setUploadingLogo(true);
      const { data } = await axios.post(`${API}/uploads/merchant-logo`, body, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setFormData((prev) => ({ ...prev, logo_path: data?.path || "" }));
    } catch (error) {
      console.error("Failed to upload merchant logo:", error);
      alert(error.response?.data?.detail || "Gagal upload logo merchant.");
    } finally {
      setUploadingLogo(false);
      event.target.value = "";
    }
  };

  const handleDelete = async (merchantId) => {
    if (!window.confirm("Hapus merchant ini?")) return;
    try {
      await axios.delete(`${API}/merchants/${merchantId}`, { withCredentials: true });
      fetchMerchants();
    } catch (error) {
      console.error("Failed to delete merchant:", error);
      alert(error.response?.data?.detail || "Gagal menghapus merchant.");
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const payload = {
      name: formData.name.trim(),
      address: formData.address.trim(),
      phone: formData.phone.trim(),
      logo_path: formData.logo_path.trim(),
    };
    if (!payload.name || !payload.address || !payload.phone) {
      alert("Nama, alamat, dan no HP wajib diisi.");
      return;
    }

    try {
      if (editingMerchant) {
        await axios.put(`${API}/merchants/${editingMerchant.id}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API}/merchants`, payload, { withCredentials: true });
      }
      setIsModalOpen(false);
      setEditingMerchant(null);
      setFormData(initialForm);
      fetchMerchants();
    } catch (error) {
      console.error("Failed to save merchant:", error);
      alert(error.response?.data?.detail || "Gagal menyimpan merchant.");
    }
  };

  return (
    <DashboardLayout>
      <Header title="Merchant" />
      <div className="space-y-6 p-8" data-testid="merchant-page">
        <section className="rounded-sm border border-white/10 bg-zinc-900/50 p-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-xl font-semibold text-white">Tabel Merchant</h2>
              <p className="mt-1 text-sm text-zinc-500">Kelola data merchant: nama, alamat, dan no HP.</p>
            </div>
            <Button onClick={openCreate} className="rounded-sm bg-[#D4AF37] font-semibold text-black hover:bg-[#C59A27]">
              <Plus className="mr-2 h-4 w-4" />
              Tambah Merchant
            </Button>
          </div>
        </section>

        <section className="overflow-hidden rounded-sm border border-white/10 bg-zinc-900/50">
          <Table>
            <TableHeader>
              <TableRow className="border-white/10 hover:bg-transparent">
                <TableHead>No</TableHead>
                <TableHead>Logo</TableHead>
                <TableHead>Nama</TableHead>
                <TableHead>Alamat</TableHead>
                <TableHead>No HP</TableHead>
                <TableHead className="text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="py-8 text-center text-sm text-zinc-500">Loading...</TableCell>
                </TableRow>
              ) : sortedMerchants.length > 0 ? (
                sortedMerchants.map((merchant, index) => (
                  <TableRow key={merchant.id || index} className="border-white/5 hover:bg-zinc-900/50">
                    <TableCell className="py-4 text-sm text-zinc-300">{index + 1}</TableCell>
                    <TableCell className="py-4">
                      {merchant.logo_path ? (
                        <a href={resolveFileUrl(merchant.logo_path)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-3">
                          <span className="grid h-12 w-12 place-items-center overflow-hidden rounded-sm border border-white/10 bg-black/30">
                            <img src={resolveFileUrl(merchant.logo_path)} alt={`Logo ${merchant.name}`} className="max-h-full max-w-full object-contain" />
                          </span>
                          <span className="text-xs font-medium text-sky-300 hover:text-sky-200">Lihat</span>
                        </a>
                      ) : (
                        <span className="text-sm text-zinc-500">Belum ada</span>
                      )}
                    </TableCell>
                    <TableCell className="py-4 text-sm font-semibold text-white">{merchant.name}</TableCell>
                    <TableCell className="py-4 text-sm text-zinc-300">
                      <p className="max-w-[420px] whitespace-pre-wrap leading-relaxed">{merchant.address}</p>
                    </TableCell>
                    <TableCell className="py-4 text-sm text-zinc-300">{merchant.phone}</TableCell>
                    <TableCell className="py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => openEdit(merchant)}
                          className="rounded-sm p-2 text-zinc-400 transition-colors hover:bg-zinc-800 hover:text-white"
                        >
                          <Edit2 className="h-4 w-4" strokeWidth={1.5} />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(merchant.id)}
                          className="rounded-sm p-2 text-zinc-400 transition-colors hover:bg-red-500/10 hover:text-red-400"
                        >
                          <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} className="py-8 text-center text-sm text-zinc-500">
                    Belum ada data merchant.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </section>

        <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
          <DialogContent className="max-w-lg border-white/10 bg-zinc-900 text-white">
            <DialogHeader>
              <DialogTitle>{editingMerchant ? "Edit Merchant" : "Tambah Merchant"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="mt-4 space-y-4">
              <div className="space-y-2">
                <Label>Nama</Label>
                <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
              </div>
              <div className="space-y-2">
                <Label>No HP</Label>
                <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} required />
              </div>
              <div className="space-y-3 rounded-sm border border-white/10 bg-black/20 p-3">
                <Label>Logo Merchant</Label>
                <Input
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
                  onChange={handleLogoUpload}
                  disabled={uploadingLogo}
                  className="cursor-pointer"
                />
                {uploadingLogo ? <p className="text-xs text-zinc-500">Mengupload logo...</p> : null}
                {formData.logo_path ? (
                  <div className="flex items-center justify-between gap-3 rounded-sm border border-[#D4AF37]/20 bg-[#D4AF37]/5 p-3">
                    <div className="flex min-w-0 items-center gap-3">
                      <span className="grid h-14 w-14 shrink-0 place-items-center overflow-hidden rounded-sm border border-white/10 bg-black/30">
                        <img src={resolveFileUrl(formData.logo_path)} alt="Logo merchant" className="max-h-full max-w-full object-contain" />
                      </span>
                      <div className="min-w-0">
                        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#F3D77A]">Logo terlampir</p>
                        <a href={resolveFileUrl(formData.logo_path)} target="_blank" rel="noreferrer" className="text-xs text-sky-300 hover:text-sky-200">
                          Lihat logo
                        </a>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setFormData((prev) => ({ ...prev, logo_path: "" }))}
                      className="rounded-sm border border-rose-500/25 bg-rose-500/10 px-3 py-2 text-xs font-semibold text-rose-300 hover:bg-rose-500/15"
                    >
                      Hapus
                    </button>
                  </div>
                ) : (
                  <p className="text-xs text-zinc-500">Belum ada logo. Jika kosong, sistem akan memakai logo default.</p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Alamat</Label>
                <Textarea
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="min-h-[100px] resize-none"
                  required
                />
              </div>
              <div className="flex gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)} className="flex-1 border-white/10 text-white hover:bg-zinc-800">
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 bg-[#D4AF37] font-semibold text-black hover:bg-[#C59A27]">
                  {editingMerchant ? "Update" : "Tambah"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
